import React, {useState} from 'react';
import {Navigate, useOutletContext} from 'react-router-dom';
import {useDispatch} from 'react-redux';
import {Button, Input, Label, FormGroup} from 'reactstrap';
import {toast} from 'react-toastify';
import {addAdminNote, deleteAdminNote, updateAdminNote} from '../../../slices/organizations.slice';
import {formatDate} from '../../../utils/dates';

const emptyForm = {title: '', body: ''};

const NoteAuthor = ({label, user, date}) => user && (
    <span className="me-3">
        <span className="fas fa-user me-2 text-primary" />
        {label} {user.firstName} {user.lastName} · {formatDate(date)}
    </span>
);

const NoteEditor = ({form, setForm, onSave, onCancel, saving}) => (
    <div className="mt-2">
        <FormGroup>
            <Label>Title</Label>
            <Input
                type="text"
                placeholder="Note title"
                value={form.title}
                onChange={e => setForm(prev => ({...prev, title: e.target.value}))}
            />
        </FormGroup>
        <FormGroup>
            <Label>Note</Label>
            <Input
                type="textarea"
                rows={4}
                placeholder="Write the note here"
                value={form.body}
                onChange={e => setForm(prev => ({...prev, body: e.target.value}))}
            />
        </FormGroup>
        <div className="d-flex justify-content-end gap-2">
            <Button color="secondary" outline onClick={onCancel} disabled={saving}>
                Cancel
            </Button>
            <Button color="primary" outline onClick={onSave} disabled={saving || !form.title.trim()}>
                <span className="fas fa-save me-2" /> Save
            </Button>
        </div>
    </div>
);

const NotesTab = () => {
    const {organization, bacView} = useOutletContext();
    const dispatch = useDispatch();
    const orgId = organization._id;

    // editing holds 'new' while adding, a note id while editing, or null.
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    if (!bacView) {
        return <Navigate to="../overview" replace />;
    }

    const notes = organization.adminNotes || [];

    const startAdd = () => {
        setForm(emptyForm);
        setEditing('new');
    };

    const startEdit = (note) => {
        setForm({title: note.title || '', body: note.body || ''});
        setEditing(note._id);
    };

    const cancel = () => {
        setEditing(null);
        setForm(emptyForm);
    };

    const run = (promise, messages) => {
        setSaving(true);
        toast.promise(promise, messages);
        return promise.then(() => cancel()).finally(() => setSaving(false));
    };

    const handleSave = () => {
        const note = {title: form.title.trim(), body: form.body.trim()};
        if (editing === 'new') {
            run(dispatch(addAdminNote({orgId, note})).unwrap(), {
                pending: 'Adding note...',
                success: 'Note added',
                error: 'Error adding note',
            });
        } else {
            run(dispatch(updateAdminNote({orgId, noteId: editing, note})).unwrap(), {
                pending: 'Updating note...',
                success: 'Note updated',
                error: 'Error updating note',
            });
        }
    };

    const handleDelete = (noteId) => {
        const promise = dispatch(deleteAdminNote({orgId, noteId})).unwrap();
        toast.promise(promise, {
            pending: 'Deleting note...',
            success: 'Note deleted',
            error: 'Error deleting note',
        });
    };

    return (
        <div className="notes-tab">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <p className="mb-0 text-uppercase text-primary">
                    <span className="fas fa-sticky-note me-2" />
                    Private notes (visible to BAC only)
                </p>
                {editing !== 'new' && (
                    <Button color="primary" outline onClick={startAdd}>
                        <span className="fas fa-plus me-2" /> Add note
                    </Button>
                )}
            </div>

            {editing === 'new' && (
                <div className="settings-panel border-start border-primary bg-broken mb-3 p-3">
                    <NoteEditor
                        form={form}
                        setForm={setForm}
                        onSave={handleSave}
                        onCancel={cancel}
                        saving={saving}
                    />
                </div>
            )}

            {!notes.length && editing !== 'new' && (
                <p className="text-white">No notes yet.</p>
            )}

            {notes.map(note => (
                <div key={note._id} className="settings-panel border-start border-primary bg-broken mb-3 p-3">
                    {editing === note._id ? (
                        <NoteEditor
                            form={form}
                            setForm={setForm}
                            onSave={handleSave}
                            onCancel={cancel}
                            saving={saving}
                        />
                    ) : (
                        <>
                            <div className="d-flex justify-content-between align-items-start">
                                <h5 className="text-primary mb-0">{note.title || 'Untitled'}</h5>
                                <div className="text-nowrap ms-3">
                                    <Button color="link" className="p-0 me-3" onClick={() => startEdit(note)}>
                                        <span className="fas fa-pencil" />
                                    </Button>
                                    <Button color="link" className="p-0 text-danger" onClick={() => handleDelete(note._id)}>
                                        <span className="fas fa-trash" />
                                    </Button>
                                </div>
                            </div>
                            {note.body && (
                                <p className="mt-2 mb-2" style={{whiteSpace: 'pre-wrap'}}>{note.body}</p>
                            )}
                            <div className="small text-white-50">
                                <NoteAuthor label="Added by" user={note.createdBy} date={note.createdAt} />
                            </div>
                        </>
                    )}
                </div>
            ))}
        </div>
    );
};

export default NotesTab;
